import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { TimelinePositionEngine } from './use-timeline-position';

interface ScrollDriverOptions {
  readonly wheelSensitivity?: number;
  readonly keyStep?: number;
}

const DEFAULT_WHEEL_SENSITIVITY = 0.02;
const DEFAULT_KEY_STEP = 4;
const LINE_HEIGHT_PX = 16;
const INERTIA_DECAY = 0.92;
const INERTIA_STOP = 0.002;
const INERTIA_START = 0.02;

export function useScrollDriver(
  engine: TimelinePositionEngine,
  targetRef: RefObject<HTMLElement>,
  options: ScrollDriverOptions = {},
): void {
  const inertiaRef = useRef<{ rafId: number | null; velocity: number }>({
    rafId: null,
    velocity: 0,
  });

  useEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    const wheelSensitivity = options.wheelSensitivity ?? DEFAULT_WHEEL_SENSITIVITY;
    const keyStep = options.keyStep ?? DEFAULT_KEY_STEP;
    const inertia = createInertiaController(engine, inertiaRef.current, wheelSensitivity);
    const touch = createTouchController(engine, inertia, wheelSensitivity);

    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const deltaPx = normalizeWheelDelta(event, element);
      engine.scrollBy(deltaPx * wheelSensitivity);
    };
    const onKeyDown = createKeyHandler(engine, keyStep);

    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('touchstart', touch.onStart, { passive: true });
    element.addEventListener('touchmove', touch.onMove, { passive: false });
    element.addEventListener('touchend', touch.onEnd);
    element.addEventListener('touchcancel', touch.onEnd);
    element.addEventListener('keydown', onKeyDown);

    return () => {
      inertia.stop();
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('touchstart', touch.onStart);
      element.removeEventListener('touchmove', touch.onMove);
      element.removeEventListener('touchend', touch.onEnd);
      element.removeEventListener('touchcancel', touch.onEnd);
      element.removeEventListener('keydown', onKeyDown);
    };
  }, [engine, options.keyStep, options.wheelSensitivity, targetRef]);
}

function createInertiaController(
  engine: TimelinePositionEngine,
  state: { rafId: number | null; velocity: number },
  sensitivity: number,
) {
  const stop = () => {
    if (state.rafId !== null) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
  };

  const start = (velocityPxPerMs: number) => {
    state.velocity = velocityPxPerMs;
    if (Math.abs(state.velocity) <= INERTIA_START) return;
    stop();
    const frame = () => {
      state.velocity *= INERTIA_DECAY;
      if (Math.abs(state.velocity) < INERTIA_STOP) {
        stop();
        return;
      }
      engine.scrollBy(state.velocity * 16 * sensitivity);
      state.rafId = requestAnimationFrame(frame);
    };
    state.rafId = requestAnimationFrame(frame);
  };

  return { stop, start };
}

function createTouchController(
  engine: TimelinePositionEngine,
  inertia: { stop: () => void; start: (velocityPxPerMs: number) => void },
  sensitivity: number,
) {
  const state = { active: false, lastY: 0, lastTs: 0, velocity: 0 };
  const onStart = (event: TouchEvent): void => {
    if (event.touches.length === 0) return;
    inertia.stop();
    state.active = true;
    state.lastY = event.touches[0].clientY;
    state.lastTs = event.timeStamp;
    state.velocity = 0;
  };
  const onMove = (event: TouchEvent): void => {
    if (!state.active || event.touches.length === 0) return;
    event.preventDefault();
    const y = event.touches[0].clientY;
    const deltaY = state.lastY - y;
    const deltaMs = Math.max(1, event.timeStamp - state.lastTs);
    state.velocity = deltaY / deltaMs;
    state.lastY = y;
    state.lastTs = event.timeStamp;
    engine.scrollBy(deltaY * sensitivity);
  };
  const onEnd = (): void => {
    if (!state.active) return;
    state.active = false;
    inertia.start(state.velocity);
  };
  return { onStart, onMove, onEnd };
}

function createKeyHandler(engine: TimelinePositionEngine, keyStep: number) {
  return (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      engine.scrollBy(keyStep);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      engine.scrollBy(-keyStep);
    } else if (event.key === 'PageDown') {
      event.preventDefault();
      engine.scrollBy(keyStep * 4);
    } else if (event.key === 'PageUp') {
      event.preventDefault();
      engine.scrollBy(-keyStep * 4);
    } else if (event.key === 'Home') {
      event.preventDefault();
      engine.setTarget(Number.NEGATIVE_INFINITY);
    } else if (event.key === 'End') {
      event.preventDefault();
      engine.setTarget(Number.POSITIVE_INFINITY);
    }
  };
}

function normalizeWheelDelta(event: WheelEvent, element: HTMLElement): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_PIXEL) {
    return event.deltaY;
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * LINE_HEIGHT_PX;
  }
  return event.deltaY * element.clientHeight;
}
